import React, {useMemo, useState} from 'react';
import {Select} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {
  PersonnelSimpleResponse,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {UserTypePills} from 'src/shared/constants/PillConstants';
import {useGetCourseDefinitionSelectOptionsQuery} from 'src/redux/queries/courseDefinition';
import {useGetCourseAssignmentPotentialCandidatesQuery} from 'src/redux/queries/courseAssignment';

/**
 * "Not assigned" view — wires the existing but previously-unused
 * `listPotentialCandidates` endpoint into the cockpit: pick one course, see
 * everyone eligible for it who has no assignment. Requires a single course
 * selection (the endpoint is course-scoped, not filter-scoped like the rest
 * of the cockpit).
 */
export const ComplianceNotAssignedPanel: React.FC = () => {
  const [courseDefinitionId, setCourseDefinitionId] = useState<string | null>(
    null
  );

  const {data: courseOptions} = useGetCourseDefinitionSelectOptionsQuery({
    start: 0,
    length: 500,
    sortColumn: 'name',
    sortDirection: 'asc',
  });
  const courseData = useMemo(
    () =>
      (courseOptions?.data ?? []).map(option => ({
        value: String(option.id),
        label: option.label ?? String(option.id),
      })),
    [courseOptions?.data]
  );

  const {data, isLoading, isFetching, isError} =
    useGetCourseAssignmentPotentialCandidatesQuery(
      {
        start: 0,
        length: 200,
        courseDefinitionId: Number(courseDefinitionId),
      },
      {skip: !courseDefinitionId}
    );
  const rows = data?.data ?? [];

  const columns = useMemo<MRT_ColumnDef<PersonnelSimpleResponse>[]>(
    () => [
      {accessorKey: 'name', header: 'Person'},
      {accessorKey: 'employeeId', header: 'Employee ID'},
      {accessorKey: 'department.name', header: 'Department'},
      {accessorKey: 'supervisor.name', header: 'Supervisor'},
      {accessorKey: 'vendor.name', header: 'Vendor'},
      {
        accessorKey: 'userType',
        header: 'User Type',
        Cell: ({cell}) => (
          <Pill {...UserTypePills(cell.getValue<UserTypeEnum>())} />
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'training-compliance-not-assigned',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      enableRowActions: false,
      columns,
      data: rows,
      getRowId: row => String(row.id),
    },
    totalCount: rows.length,
    isError,
    isLoading,
    isFetching,
  });

  return (
    <Box>
      <Box mb="md" style={{width: '320px'}}>
        <Select
          label="Course"
          placeholder="Select a course"
          data={courseData}
          value={courseDefinitionId}
          onChange={setCourseDefinitionId}
          searchable
          clearable
        />
      </Box>
      {courseDefinitionId ? (
        table
      ) : (
        <Alert color="blue">
          Select a course to see who is eligible but not currently assigned.
        </Alert>
      )}
    </Box>
  );
};
